import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/site/SiteLayout";
import Seo, { breadcrumbLd, ogUrl } from "@/components/site/Seo";
import { useCurrency, money } from "@/lib/currency";
import { API } from "@/lib/api";
import { track } from "@/lib/analytics";
import { BookCover } from "@/components/site/PublicPresentation";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { BookOpen, ArrowUpRight } from "lucide-react";

const FILTERS = ["All", "Free", "Paid", "China", "India", "Fairs"];

function GateModal({ onClose, onDone }) {
  const [form, setForm] = useState({ name: "", email: "", whatsapp: "", country: "", sourcing: "", newsletter: true });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [links, setLinks] = useState([]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { setError("Please enter your name and email."); setStatus("error"); return; }
    setStatus("sending");
    try {
      const r = await fetch(`${API}/guides/free-unlock`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form),
      });
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail || "Could not unlock the library");
        track("guide_download", { slug: "free-all" });
      try { localStorage.setItem("atlas_email", form.email); localStorage.setItem("atlas_name", form.name); } catch {}
      setLinks(d.downloads || []);
      setStatus("done");
      onDone && onDone(form.email);
    } catch (e) { setError(e.message || "We couldn't unlock your library. Please try again."); setStatus("error"); }
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="atlas-public z-[220] max-h-[90dvh] overflow-auto" style={modal} data-testid="guide-gate-modal">
        <DialogTitle className="sr-only">Unlock the free Atlas library</DialogTitle>
        <DialogDescription className="sr-only">Enter your name and email to access the free guides.</DialogDescription>
        {status === "done" ? (
          <div>
            <h3 style={{ color: "var(--navy)", fontSize: 22 }}>✓ Your library is unlocked</h3>
            <p style={{ color: "var(--grey)", margin: "8px 0 14px", fontSize: 14 }}>
              We've emailed every guide. Download links below work for 30 days.
            </p>
            <div style={{ maxHeight: 260, overflow: "auto" }}>
              {links.map((l) => (
                <a key={l.slug} href={l.url} className="btn btn-line" data-testid={`free-dl-${l.slug}`}
                   style={{ display: "block", textAlign: "left", marginBottom: 8, padding: "10px 14px", fontSize: 13 }}>
                  ⬇ {l.title}
                </a>
              ))}
            </div>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className="fc-k" style={{ color: "#8b703e", fontSize: 10, letterSpacing: ".12em" }}>THE COMPLIMENTARY COLLECTION</div>
            <h3 style={{ color: "var(--navy)", margin: "8px 0 18px" }}>Unlock your free library.</h3>
            <input placeholder="Your Name" aria-label="Your name" required value={form.name} onChange={set("name")} style={gi} data-testid="gate-name" />
            <input placeholder="Email Address" aria-label="Email address" type="email" required value={form.email} onChange={set("email")} style={gi} data-testid="gate-email" />
            <input placeholder="WhatsApp Number" aria-label="WhatsApp number" type="tel" value={form.whatsapp} onChange={set("whatsapp")} style={gi} data-testid="gate-whatsapp" />
            <input placeholder="Your country" aria-label="Your country" value={form.country} onChange={set("country")} style={gi} data-testid="gate-country" />
            <input placeholder="What do you source?" aria-label="What do you source?" value={form.sourcing} onChange={set("sourcing")} style={gi} data-testid="gate-sourcing" />
            <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12.5, color: "var(--grey)", margin: "2px 0 10px", cursor: "pointer" }}>
              <input type="checkbox" checked={form.newsletter} onChange={(e) => setForm({ ...form, newsletter: e.target.checked })} data-testid="gate-newsletter" style={{ marginTop: 2 }} />
              <span>Email me occasional sourcing tips &amp; new guides. Unsubscribe anytime.</span>
            </label>
            {status === "error" && <p role="alert" data-testid="guide-gate-error" style={{ color: "#a53b35", fontSize: 13 }}>{error}</p>}
            <button className="btn btn-red" style={{ width: "100%" }} disabled={status === "sending"} data-testid="gate-submit">
              {status === "sending" ? "Unlocking…" : "Unlock the Library"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Bundles({ bundles, currency }) {
  if (!bundles.length) return null;
  return (
    <div className="bundles-grid" data-testid="bundles-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))", gap: 16, margin: "8px 0 34px" }}>
      {bundles.map((b) => (
        <div key={b.slug} data-testid={`bundle-${b.slug}`}
             style={{ background: b.is_complete_library ? "#0B2649" : "#fff", color: b.is_complete_library ? "#fff" : "var(--navy)",
                      border: b.is_complete_library ? "none" : "1.5px solid var(--line)", borderRadius: 14, padding: 22, position: "relative" }}>
          {b.is_complete_library && <span style={{ position: "absolute", top: 14, right: 14, background: "#C08A2E", color: "#2A1D06", fontWeight: 800, fontSize: 11, padding: "3px 9px", borderRadius: 999 }}>BEST VALUE</span>}
          <h3 style={{ fontSize: 20, marginBottom: 4 }}>{b.name}</h3>
          <p style={{ fontSize: 13, color: b.is_complete_library ? "#CFE0F4" : "var(--grey)", minHeight: 34 }}>
            {b.is_complete_library ? "Every paid guide + every new guide free for 12 months." : `${b.guide_slugs.length} guides bundled.`}
          </p>
          <div style={{ margin: "10px 0 14px" }}>
            <span style={{ fontSize: 26, fontWeight: 800 }}>{money(currency, b.price)}</span>
            {b.was > b.price && <span style={{ textDecoration: "line-through", opacity: 0.6, marginLeft: 10, fontSize: 15 }}>{money(currency, b.was)}</span>}
          </div>
          <Link className={"btn " + (b.is_complete_library ? "btn-wht" : "btn-navy")} to={`/checkout/bundle/${b.slug}`}
                style={{ width: "100%" }} data-testid={`buy-bundle-${b.slug}`} onClick={() => track("checkout_started", { bundle: b.slug })}>
            Get the {b.name}
          </Link>
        </div>
      ))}
    </div>
  );
}

export default function Guides() {
  const { currency, country, switchToInr } = useCurrency();
  const [catalogError, setCatalogError] = useState(false);
  const [data, setData] = useState({ guides: [], bundles: [] });
  const [active, setActive] = useState("All");
  const [gate, setGate] = useState(false);
  const [owned, setOwned] = useState({ free_unlocked: false, owned: [] });
  const [downloadError, setDownloadError] = useState("");

  useEffect(() => {
    fetch(`${API}/guides`, { credentials: "include" }).then((r) => { if (!r.ok) throw new Error(); return r.json(); }).then(setData).catch(() => setCatalogError(true));
    const email = localStorage.getItem("atlas_email");
    if (email) fetch(`${API}/guides/access?email=${encodeURIComponent(email)}`).then((r) => r.json()).then(setOwned).catch(() => {});
  }, [currency]);

  const filtered = data.guides.filter((g) => active === "All" || (g.filters || []).includes(active.toLowerCase()));

  const onFree = async (g) => {
    setDownloadError("");
    try {
    const email = localStorage.getItem("atlas_email");
    if (email && owned.free_unlocked) {
      const r = await fetch(`${API}/guides/free-unlock`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name: localStorage.getItem("atlas_name") || "Reader", email }) });
      if (!r.ok) throw new Error("Could not unlock the library");
      const d = await r.json();
      const link = (d.downloads || []).find((x) => x.slug === g.slug);
      if (!link) throw new Error("Download unavailable");
      track("guide_download", { slug: g.slug });
      window.location.href = link.url;
    } else {
      setGate(true);
    }
    } catch { setDownloadError("We couldn't prepare your download. Please try again."); }
  };

  const doSwitch = async () => {
    try { await switchToInr(); } catch (e) { alert(e.message); }
  };

  return (
    <SiteLayout>
      <Seo
        title="Atlas Library — Free & Premium Sourcing Guides | The Sourcing Atlas"
        description="Download free sourcing guides and buy field manuals and toolkits. The China & India Sourcing Atlases, trade-fair almanacs, the Canton Fair Field Manual and more."
        path="/guides"
        ogImage={ogUrl("Free China & India Sourcing Guides", "The Library")}
        jsonLd={[breadcrumbLd([{ name: "Home", path: "/" }, { name: "Guides", path: "/guides" }])]}
      />
      <div className="crumb"><Link to="/">Home</Link> › Guides</div>
      <div className="pband">
        <div className="pband-in">
          <span className="tierpill tn">THE ATLAS LIBRARY</span>
          <h1 data-testid="guides-title">Better intelligence.<br />Better buying decisions.</h1>
          <p className="lede">Country atlases, field manuals and practical toolkits. Start with a free guide, go deeper with a premium edition, or build your library with a bundle.</p>
        </div>
      </div>

      <section>
        <div className="wrap">
          {catalogError && <div role="alert" data-testid="guide-catalog-error" style={{ padding: 24, border: "1px solid var(--line)", marginBottom: 24 }}><p>The library is temporarily unavailable. Please try again shortly.</p><button className="btn btn-line" data-testid="guide-catalog-retry" onClick={() => window.location.reload()}>Try again</button></div>}
          <div className="at-guide-intro"><span data-testid="guide-catalog-count"><BookOpen size={16} style={{ display: "inline", marginRight: 8 }} /> {data.guides.length} publications · Digital editions for real-world sourcing</span><Link className="at-text-link" to="/guides/my-library" data-testid="my-library-link">Already a reader? Open your library<ArrowUpRight size={15} /></Link></div>
          <div className="filters" data-testid="guide-filters">
            {FILTERS.map((f) => (
              <button key={f} className={active === f ? "on" : ""} onClick={() => setActive(f)} aria-pressed={active === f} data-testid={`filter-${f.toLowerCase()}`}>{f === "Paid" ? "Premium" : f}</button>
            ))}
          </div>
          {downloadError && <p role="alert" data-testid="guide-download-error" style={{ color: "#a53b35", marginBottom: 20 }}>{downloadError}</p>}
          <div className="at-guide-grid">
            {filtered.map((g) => {
              const isOwned = owned.owned?.includes(g.slug);
              return <article className="at-publication" key={g.slug} data-testid={`guide-${g.slug}`}>
                <Link to={`/guides/${g.slug}`} data-testid={`guide-cover-link-${g.slug}`}><BookCover guide={g} prefix="guide" /></Link>
                <div className="at-publication-info"><span className={`at-guide-badge ${g.tag === "free" ? "free" : "premium"}`} data-testid={`guide-badge-${g.slug}`}>{g.tag === "free" ? "FREE ATLAS" : "PREMIUM EDITION"}</span><h3>{g.title}</h3><p>{g.blurb}</p><span className="at-guide-price" data-testid={`guide-price-${g.slug}`}>{g.tag === "free" ? "Complimentary" : money(currency, g.price)}</span>
                <div className="at-guide-actions"><Link className="btn btn-line" to={`/guides/${g.slug}`} data-testid={`guide-read-${g.slug}`}>Explore guide<ArrowUpRight size={14} /></Link>
                  {g.tag === "free" ? <button className="btn btn-red" onClick={() => onFree(g)} data-testid={`download-${g.slug}`}>Download</button> : isOwned ? <Link className="btn btn-navy" to="/guides/my-library" data-testid={`guide-owned-${g.slug}`}>In your library</Link> : <Link className="btn btn-navy" to={`/checkout/guide/${g.slug}`} onClick={() => track("checkout_started", { guide: g.slug })} data-testid={`guide-buy-${g.slug}`}>Get the guide</Link>}
                </div></div>
              </article>;
            })}
          </div>
          <div className="at-bundle-heading"><span className="at-eyebrow">GO FURTHER, TOGETHER</span><h2>Build your sourcing library.</h2><p style={{ color: "var(--grey)", fontSize: 14, marginBottom: 24 }}>Complementary intelligence, brought together in our existing guide bundles.</p></div>
          <Bundles bundles={data.bundles} currency={currency} />
          {country !== "IN" && (
            <p style={{ textAlign: "center", marginTop: 24, fontSize: 13, color: "var(--grey)" }}>
              Paying from India?{" "}
              <button onClick={doSwitch} data-testid="switch-inr" style={{ background: "none", border: "none", color: "var(--navy)", fontWeight: 700, cursor: "pointer", textDecoration: "underline" }}>Switch to ₹</button>
            </p>
          )}
        </div>
      </section>
      {gate && <GateModal onClose={() => setGate(false)} onDone={(email) => fetch(`${API}/guides/access?email=${encodeURIComponent(email)}`).then((r) => r.json()).then(setOwned)} />}
    </SiteLayout>
  );
}

const modal = { background: "#fff", borderRadius: 16, padding: 28, width: 420, maxWidth: "100%", boxShadow: "0 26px 70px rgba(9,25,55,.4)" };
const gi = { width: "100%", border: "1.5px solid var(--line)", borderRadius: 10, padding: "12px 13px", fontSize: 14, marginBottom: 10, fontFamily: "inherit", background: "#FCFDFF" };
